/**
 * Central helper til alle besøgendes indsendelsesformularer.
 *
 * - Logger alle fejl til `public.submission_errors` (synlig i /admin/fejl-i-formularer)
 * - Sender også fejl til Sentry med rige tags (form, table, pg_code, http_status)
 * - Mapper kendte Postgres-/HTTP-koder til pænere danske beskeder
 * - Beskytter mod log-spam med simpel in-memory rate-limit
 */
import { supabase } from "@/integrations/supabase/client";
import { Sentry } from "@/lib/sentry";

export interface SubmissionError {
  code?: string | null;
  message?: string | null;
  details?: string | null;
  hint?: string | null;
  status?: number | null;
}

export interface SubmissionResult<T> {
  ok: boolean;
  data?: T;
  /** Brugervenlig dansk besked, klar til toast.error(...) */
  userMessage?: string;
  /** Den rå Supabase/PostgREST fejl, hvis kalderen vil inspicere den */
  error?: SubmissionError;
}

export interface ChangeRequestPayload {
  entity_type: "model" | "variant" | "price_update";
  entity_id?: string | null;
  parent_model_id?: string | null;
  proposed_data: Record<string, unknown>;
  source_url?: string | null;
  notes?: string | null;
  submitter_email?: string;
  submitter_name?: string;
  save_as_draft?: boolean;
  website?: string;
}

export interface ChangeRequestResponse {
  id: string;
  duplicate_warning?: boolean;
}

interface SubmitOptions<T> {
  /** Stabilt, kort id på formularen (snake_case), fx "report_car_error" */
  formName: string;
  /** Tabel-/edge-function navn der blev ramt — fx "feedback" eller "fn:newsletter-subscribe" */
  table?: string;
  /** Rå payload — bruges KUN til at udlede felt-navne/længder, indhold gemmes IKKE */
  payload?: Record<string, unknown>;
  /** Brugerens email (hvis formularen har et email-felt) — gemmes i loggen */
  email?: string | null;
  /** Ekstra metadata-felter der må logges råt (fx { entity_type: "variant" }) */
  meta?: Record<string, string | number | boolean | null | undefined>;
  /** Funktionen der udfører selve indsendelsen */
  perform: () => PromiseLike<{ data?: T | null; error: SubmissionError | null }>;
}

// In-memory rate limit: maks 1 log pr. (form,code) pr. 10 sek
const lastLoggedAt = new Map<string, number>();
const RATE_LIMIT_MS = 10_000;

function shouldLog(key: string): boolean {
  const now = Date.now();
  const last = lastLoggedAt.get(key) ?? 0;
  if (now - last < RATE_LIMIT_MS) return false;
  lastLoggedAt.set(key, now);
  return true;
}

function summarisePayload(
  payload: Record<string, unknown> | undefined,
  meta: SubmitOptions<unknown>["meta"]
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (payload) {
    for (const [k, v] of Object.entries(payload)) {
      if (v === null || v === undefined) {
        out[k] = { type: "null" };
      } else if (typeof v === "string") {
        out[k] = { type: "string", len: v.length };
      } else if (typeof v === "number" || typeof v === "boolean") {
        out[k] = { type: typeof v };
      } else if (Array.isArray(v)) {
        out[k] = { type: "array", len: v.length };
      } else {
        out[k] = { type: "object", keys: Object.keys(v as object).length };
      }
    }
  }
  if (meta) {
    out._meta = meta;
  }
  return out;
}

function mapUserMessage(err: SubmissionError | null | undefined): string {
  if (!err) return "Der opstod en fejl. Prøv venligst igen.";
  const code = err.code ?? "";
  const status = err.status ?? 0;

  if (code === "23502") return "Et påkrævet felt mangler. Tjek formularen og prøv igen.";
  if (code === "23514") return "En af værdierne er ikke tilladt. Tjek formularen og prøv igen.";
  if (code === "23505") return "Det her er allerede indsendt — tak!";
  if (code === "42501" || status === 401 || status === 403) {
    return "Du har ikke rettigheder til denne handling. Prøv at logge ind igen.";
  }
  if (status === 429) return "Du har sendt for mange beskeder — prøv igen om lidt.";
  if (status >= 500) return "Vores server svarer ikke. Prøv venligst igen om et øjeblik.";
  return "Der opstod en fejl. Prøv venligst igen.";
}

async function logToBackend(row: {
  form_name: string;
  target_table?: string;
  http_status?: number | null;
  pg_code?: string | null;
  pg_message?: string | null;
  pg_hint?: string | null;
  payload_summary: Record<string, unknown>;
  email?: string | null;
}) {
  const key = `${row.form_name}:${row.pg_code ?? row.http_status ?? "?"}`;
  if (!shouldLog(key)) return;

  try {
    const { data: { user } } = await supabase.auth.getUser();
    // Cast: types regenereres efter migration, men vi vil ikke blokere på det
    await (supabase.from("submission_errors") as unknown as {
      insert: (row: Record<string, unknown>) => Promise<{ error: unknown }>;
    }).insert({
      form_name: row.form_name,
      target_table: row.target_table ?? null,
      http_status: row.http_status ?? null,
      pg_code: row.pg_code ?? null,
      pg_message: row.pg_message ?? null,
      pg_hint: row.pg_hint ?? null,
      route: typeof window !== "undefined" ? window.location.pathname + window.location.search : null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
      user_id: user?.id ?? null,
      payload_summary: row.payload_summary,
    });
  } catch (logErr) {
    // Bevidst stille — undgå at fejl-i-fejl-håndtering kaster mere
    // eslint-disable-next-line no-console
    console.error("[submission] failed to write submission_errors:", logErr);
  }
}

export async function submitForm<T>(opts: SubmitOptions<T>): Promise<SubmissionResult<T>> {
  Sentry.addBreadcrumb?.({
    category: "form-submit",
    message: opts.formName,
    level: "info",
    data: { table: opts.table, fields: opts.payload ? Object.keys(opts.payload) : [] },
  });

  let result: { data?: T | null; error: SubmissionError | null };
  try {
    result = await Promise.resolve(opts.perform());
  } catch (thrown) {
    const e = thrown as Error & { status?: number; code?: string };
    result = {
      error: {
        code: e.code,
        message: e.message ?? String(thrown),
        status: e.status ?? null,
      },
    };
  }

  if (!result.error) {
    return { ok: true, data: result.data ?? undefined };
  }

  const err = result.error;
  const summary = summarisePayload(opts.payload, opts.meta);

  // eslint-disable-next-line no-console
  console.error(`[submission:${opts.formName}]`, {
    table: opts.table,
    code: err.code,
    status: err.status,
    message: err.message,
    hint: err.hint,
    payload: summary,
  });

  Sentry.captureException?.(
    new Error(`Form submission failed: ${opts.formName} (${err.code ?? err.status ?? "unknown"})`),
    {
      tags: {
        form: opts.formName,
        target_table: opts.table ?? "unknown",
        pg_code: err.code ?? "none",
        http_status: String(err.status ?? "none"),
      },
      extra: {
        message: err.message,
        hint: err.hint,
        details: err.details,
        payload_summary: summary,
      },
    }
  );

  // Fire-and-forget — vi venter ikke på log-skrivning
  void logToBackend({
    form_name: opts.formName,
    target_table: opts.table,
    http_status: err.status ?? null,
    pg_code: err.code ?? null,
    pg_message: err.message ?? null,
    pg_hint: err.hint ?? null,
    payload_summary: summary,
    email: opts.email ?? null,
  });

  return {
    ok: false,
    userMessage: mapUserMessage(err),
    error: err,
  };
}

/** Fælles, fejlrapporteret indgang til alle dataforslag. */
export async function submitChangeRequest(
  formName: string,
  payload: ChangeRequestPayload,
): Promise<SubmissionResult<ChangeRequestResponse>> {
  return submitForm<ChangeRequestResponse>({
    formName,
    table: "fn:submit-change-request",
    payload: payload.proposed_data,
    email: payload.submitter_email,
    meta: {
      entity_type: payload.entity_type,
      has_entity_id: Boolean(payload.entity_id),
      is_draft: Boolean(payload.save_as_draft),
    },
    perform: async () => {
      const result = await supabase.functions.invoke<ChangeRequestResponse & { error?: string }>(
        "submit-change-request",
        { body: payload },
      );
      if (!result.error && result.data?.error) {
        return { error: { message: result.data.error, status: 400 } };
      }
      return result;
    },
  });
}
